import {
  AllowedCommandsResponse,
  ClientFilesResponse,
  Command,
  CompanyReference,
  CTEyeClientModel,
  CTEyeClientSummaryModel,
  CTEyeClientResponse,
  CTEyeInspectionSummaryModel,
  CTEyeSubscription,
  DataFileDto,
  GaugeConfig,
  HeartbeatItemResponse,
  HeartbeatModel,
  HeartbeatRequest,
  ImportInspectionRequest,
  ImportInspectionResponse,
  ImportIntoInspectionRequest,
  ImportableInspectionModel,
  LiveInspectionResponse,
  LiveViewSessionResponse,
  ODataResponse,
  PurgeHeartbeatsResponse,
  QueuedCommandCreateRequest,
  RegisterClientRequest,
  StartLiveViewRequest,
  StartLiveViewResponse,
  SubscriptionUsageResponse,
} from "../data/entities";
import { CTEyeRepo } from "../data/repo/CTEyeRepo";

export class CTEyeService {
  /**
   * Get all CTEye clients
   */
  async getAllClients(): Promise<CTEyeClientResponse[]> {
    const response = await new CTEyeRepo().getAllClients();
    return response ?? [];
  }

  /**
   * Get CTEye clients for a specific company
   */
  async getClientsByCompanyId(companyId: number): Promise<CTEyeClientResponse[]> {
    const response = await new CTEyeRepo().getClientsByCompanyId(companyId);
    return response ?? [];
  }

  /**
   * Get heartbeats for a specific client
   */
  async getHeartbeats(
    clientId: string,
    afterDate?: string
  ): Promise<HeartbeatItemResponse[]> {
    const response = await new CTEyeRepo().getHeartbeats(clientId, afterDate);
    return response ?? [];
  }

  /**
   * Get single latest heartbeats for a specific client
   */
  async getLatestHeartbeat(clientId: string): Promise<HeartbeatItemResponse | null> {
      return await new CTEyeRepo().getLatestHeartbeat(clientId);
  }
  /**
   * Process a heartbeat from a CTEye client
   */
  async heartbeat(request: HeartbeatRequest): Promise<Command[]> {
    const response = await new CTEyeRepo().heartbeat(request);
    return response ?? [];
  }

  /**
   * Queue a command to be sent to a CTEye client
   */
  async queueCommand(request: QueuedCommandCreateRequest): Promise<boolean> {
    return await new CTEyeRepo().queueCommand(request);
  }

  /**
   * Import an inspection from a CTEye data file
   */
  async importInspection(
    clientId: string,
    request: ImportInspectionRequest
  ): Promise<ImportInspectionResponse | null> {
    return await new CTEyeRepo().importInspection(clientId, request);
  }

  /**
   * Import a CTEye data file into an existing inspection, replacing its data
   */
  async importIntoInspection(
    clientId: string,
    inspectionId: number,
    request: ImportIntoInspectionRequest
  ): Promise<ImportInspectionResponse | null> {
    return await new CTEyeRepo().importIntoInspection(clientId, inspectionId, request);
  }

  /**
   * List inspections that data can be imported into for this client's company
   */
  async getImportableInspections(clientId: string): Promise<ImportableInspectionModel[] | null> {
    return await new CTEyeRepo().getImportableInspections(clientId);
  }

  /**
   * Purge heartbeats for a specific client before a given date
   */
  async purgeHeartbeats(
    clientId: string,
    beforeDate: string
  ): Promise<PurgeHeartbeatsResponse | null> {
    return await new CTEyeRepo().purgeHeartbeats(clientId, beforeDate);
  }

  /**
   * Get allowed commands for a specific client
   */
  async getAllowedCommands(clientId: string): Promise<AllowedCommandsResponse | null> {
    return await new CTEyeRepo().getAllowedCommands(clientId);
  }

  /**
   * Get files for a specific client
   */
  async getClientFiles(clientId: string): Promise<ClientFilesResponse | null> {
    return await new CTEyeRepo().getClientFiles(clientId);
  }

    /**
   * Get all unregistered CTEye clients
   */
  async getUnregisteredClients(): Promise<CTEyeClientResponse[]> {
    const response = await new CTEyeRepo().getUnregisteredClients();
    return response ?? [];
  }

  /**
   * Register a CTEye client 
   */
  async registerClient(clientId: string, request: RegisterClientRequest): Promise<boolean> {
    return await new CTEyeRepo().registerClient(clientId, request);
  }

  /**
   * Update a CTEye client
   */
  async updateClient(
    clientId: string,
    request: { description: string; companyId?: number; logAllHeartbeats: boolean; monthlyInspectionLimit: number; monthlyReportLimit: number; gaugeSettings?: GaugeConfig }
  ): Promise<CTEyeClientResponse | null> {
    return await new CTEyeRepo().updateClient(clientId, request);
  }

  /**
   * Disable a CTEye client
   */
  async disableClient(clientId: string): Promise<boolean> {
    return await new CTEyeRepo().disableClient(clientId);
  }

  /**
   * Enable a CTEye client
   */
  async enableClient(clientId: string): Promise<boolean> {
    return await new CTEyeRepo().enableClient(clientId);
  }

    /**
   * POST /CTEye/clients/{clientId}/upload-sessions/{sessionId}/dismiss
   * Dismiss an upload session for a specific client
   */
  async dismissUploadSession(clientId: string, sessionId: string): Promise<boolean> {
    return await new CTEyeRepo().dismissUploadSession(clientId, sessionId);
  }

  /**
   * Start a live view session for a specific client
   */
  async startLiveView(request: StartLiveViewRequest): Promise<StartLiveViewResponse | null> {
    return await new CTEyeRepo().startLiveView(request);
  }

  /**
   * Poll live view data and update LastRequestedDate
   */
  async getLiveViewData(sessionId: string): Promise<LiveViewSessionResponse | null> {
    return await new CTEyeRepo().getLiveViewData(sessionId);
  }


  // ============================================
  // OData Methods (/OData/CTEyeClients)
  // ============================================

  /**
   * Get all CTEye clients with OData query support
   */
  async getClientsOData(oDataQuery: string = ""): Promise<ODataResponse<CTEyeClientModel>> {
    return await new CTEyeRepo().getClientsOData(oDataQuery);
  }

  /**
   * Get CTEye client summaries (with subscription usage) with OData query support
   */
  async getClientsSummariesOData(oDataQuery: string = ""): Promise<ODataResponse<CTEyeClientSummaryModel>> {
    return await new CTEyeRepo().getClientsSummariesOData(oDataQuery);
  }

  /**
   * Get a single CTEye client by ID (OData)
   */
  async getClientByIdOData(clientId: string): Promise<CTEyeClientModel | null> {
    return await new CTEyeRepo().getClientByIdOData(clientId);
  }

  /**
   * Get a client's gauge config 
   */
  async getClientGaugeConfig(clientId: string): Promise<GaugeConfig | null> {
    const raw = (await new CTEyeRepo().getClientById(clientId))?.gaugeSettings;
    if (!raw) return null;
    return typeof raw === "string" ? JSON.parse(raw) : raw;
  }

  /**
   * Get heartbeats for a specific client with OData query support
   */
  async getClientHeartbeatsOData(
    clientId: string,
    oDataQuery: string = ""
  ): Promise<ODataResponse<HeartbeatModel>> {
    return await new CTEyeRepo().getClientHeartbeatsOData(clientId, oDataQuery);
  }

  /**
   * Get the company associated with a specific client (OData)
   */
  async getClientCompanyOData(clientId: string): Promise<CompanyReference | null> {
    return await new CTEyeRepo().getClientCompanyOData(clientId);
  }

  /**
   * Get data files for a specific client with OData query support
   */
  async getClientDataFilesOData(
    clientId: string, 
    oDataQuery: string = ""
  ): Promise<ODataResponse<DataFileDto>> {
    return await new CTEyeRepo().getClientDataFilesOData(clientId, oDataQuery);
  }

  /**
   * Get all CTEye inspections for a specific client (OData)
   * GET /OData/CTEyeInspections(guid)/ByClient
   */
  async getInspectionsByClientOData(clientId: string, oDataQuery: string = ""): Promise<ODataResponse<any>> {
    return await new CTEyeRepo().getInspectionsByClientOData(clientId, oDataQuery);
  }

  async getInspectionSummariesByClient(clientId: string, oDataQuery: string = ""): Promise<ODataResponse<CTEyeInspectionSummaryModel>> {
    return await new CTEyeRepo().getInspectionSummariesByClient(clientId, oDataQuery);
  }

    /**
   * Get the most recent completed upload session per client (OData)
   * GET /OData/UploadSessions/Recent
   */
  async getRecentUploadSessionsOData(oDataQuery: string = ""): Promise<ODataResponse<any>> {
    return await new CTEyeRepo().getRecentUploadSessionsOData(oDataQuery);
  }

  /**
   * Get all upload sessions with OData query support
   */
  async getUploadSessionsOData(oDataQuery: string = ""): Promise<ODataResponse<any>> {
    return await new CTEyeRepo().getUploadSessionsOData(oDataQuery);
  }

  /**
   * Get upload sessions for a specific client with OData query support
   */
  async getClientUploadSessionsOData(
    clientId: string,
    oDataQuery: string = ""
  ): Promise<ODataResponse<any>> {
    return await new CTEyeRepo().getClientUploadSessionsOData(clientId, oDataQuery);
  }

  /**
   * Get the most recent inspection per client (OData)
   * GET /OData/CTEyeInspections/Default.MostRecentFilesByClient
   */
  async getMostRecentFilesByClientOData(oDataQuery: string = ""): Promise<ODataResponse<any>> {
    return await new CTEyeRepo().getMostRecentFilesByClientOData(oDataQuery);
  }

  /**
   * Get live inspections
   */
  async getLiveInspections(): Promise<LiveInspectionResponse[]> {
    const response = await new CTEyeRepo().getLiveInspections();
    return response ?? [];
  }

  /**
   * Get live inspections for a specific client
   */
  async getLiveInspectionsByClient(clientId: string): Promise<LiveInspectionResponse[]> {
    const response = await new CTEyeRepo().getLiveInspectionsByClient(clientId);
    return response ?? [];
  }

  // ============================================
  // Subscription Methods
  // ============================================

  /**
   * Get the active subscription for a specific client
   */
  async getActiveSubscription(clientId: string): Promise<CTEyeSubscription | null> {
    return await new CTEyeRepo().getActiveSubscription(clientId);
  }

  /**
   * Get subscription usage for the current user's company
   */
  async getSubscriptionUsage(): Promise<SubscriptionUsageResponse[]> {
    return await new CTEyeRepo().getSubscriptionUsage();
  }
  
  /**
   * Update the subscription for a specific client
   */
  async updateSubscription(clientId: string, request: CTEyeSubscription): Promise<CTEyeSubscription | null> {
    return await new CTEyeRepo().updateSubscription(clientId, request);
  }

}

export default CTEyeService;
